import { Apartment } from "@boark/shared/types/apartment";
import { SubletWithHoldingOrLeaseAndApartment } from "@boark/shared/types/sublet-with-holding-or-lease-and-apartment";
import { formatResidencyName } from "@boark/shared/utils/apartment-residency";
import { Outlet, useSearchParams } from "react-router-dom";

import { ApartmentLink } from "../../components/apartment-link";
import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { ResidencyName } from "../../components/residency-name";
import { Column, Filter, Table } from "../../components/table";
import { useGetApartments } from "../../queries/get-apartments";
import { useGetSublets } from "../../queries/sublet";
import { formatApartmentLabel } from "../../services/apartment";
import { useLocale } from "../../stores/locale";
import {
  compareContractLength,
  formatContractLength,
} from "../../utils/contract-length";
import { createIssueRouteForRecord } from "../../utils/create-issue-route";
import { formatDate } from "../../utils/date";
import { isCurrent } from "../../utils/is-current";
import { recordIdToId } from "../../utils/record-id-to-id";

type SubletWithHoldingOrLeaseAndApartmentData =
  SubletWithHoldingOrLeaseAndApartment & {
    apartment: Apartment;
    active: boolean;
  };

export function Sublets() {
  const { locale: t, get: getLocale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: sublets, isLoading: isSubletsLoading } = useGetSublets();
  const { data: apartments } = useGetApartments();

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  return (
    <>
      <Outlet />
      <div className="w-full min-h-full overflow-x-auto">
        <div className="flex justify-between items-center">
          <H1>{t.sublet.sublets}</H1>
          <Button route="/admin/register/sublet/create">
            {t.sublet.create}
          </Button>
        </div>
        <Table
          actions={(row) => [
            {
              label: getLocale("sublet.viewApartment"),
              route: `/admin/register/apartment/?filterBy=${encodeURIComponent(
                JSON.stringify([
                  {
                    field: "index",
                    value: row.apartment.index,
                    operator: "eq",
                  },
                ]),
              )}`,
            },
            {
              label: t.issue.create,
              route: createIssueRouteForRecord({
                type: "sublet",
                recordId: recordIdToId(row.id),
                label: `${formatApartmentLabel(row.apartment)} — ${formatResidencyName(row)} (${formatDate(row.fromDate)})`,
              }),
            },
          ]}
          data={(sublets ?? []).map((sublet) => {
            let apartment: Apartment | undefined;
            if ("holding" in sublet) {
              apartment = sublet.holding.apartment;
            } else if ("lease" in sublet) {
              apartment = sublet.lease.apartment;
            }

            if (!apartment) {
              throw new Error("Apartment not found");
            }

            return {
              ...sublet,
              apartment,
              active: isCurrent(sublet),
            };
          })}
          editRouteButton={(row) =>
            `/admin/register/sublet/${recordIdToId(row.id)}/edit`
          }
          filterBy={
            JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]
          }
          loading={isSubletsLoading}
          locale={{ yes: t.yes, no: t.no, actions: t.action.actions, empty: t.empty }}
          removeRouteButton={(row) =>
            `/admin/register/sublet/${recordIdToId(row.id)}/delete`
          }
          sortBy={searchParams.get("sortBy") || "apartment.index"}
          sortByDirection={
            searchParams.get("sortByDirection") as "asc" | "desc"
          }
          onFilter={(value: Filter[]) => {
            updateSearchParams({
              filterBy: JSON.stringify(value),
            });
          }}
          onSort={(field, direction) => {
            updateSearchParams({
              sortBy: field,
              sortByDirection: direction,
            });
          }}
        >
          <Column
            body={(row: SubletWithHoldingOrLeaseAndApartmentData) => (
              <ApartmentLink apartment={row.apartment} />
            )}
            field="apartment.index"
            filterableBy="select"
            filterOptions={(
              data: SubletWithHoldingOrLeaseAndApartmentData[],
            ) =>
              apartments
                ?.filter((apartment) =>
                  data.some(
                    (sublet: SubletWithHoldingOrLeaseAndApartmentData) =>
                      sublet.apartment.id === apartment.id,
                  ),
                )
                .map((apartment) => ({
                  label: (
                    <>
                      #{apartment.index} - {apartment.streetNumber}:
                      {apartment.apartmentNumber} ({apartment.objectNumber})
                    </>
                  ),
                  value: apartment.index,
                })) || []
            }
          >
            {t.apartment.apartment}
          </Column>
          <Column
            body={(row: SubletWithHoldingOrLeaseAndApartmentData) => (
              <ResidencyName residency={row} />
            )}
            field="name"
            sortCallback={(
              a: SubletWithHoldingOrLeaseAndApartmentData,
              b: SubletWithHoldingOrLeaseAndApartmentData,
              _field: string,
              direction: "asc" | "desc",
            ) => {
              const result = formatResidencyName(a).localeCompare(
                formatResidencyName(b),
              );
              if (direction === "asc") {
                return result;
              }
              return -result;
            }}
          >
            {getLocale("sublet.residents")}
          </Column>
          <Column
            body={(row: SubletWithHoldingOrLeaseAndApartmentData) =>
              formatDate(row.fromDate)
            }
            field="fromDate"
            filterableBy="date"
          >
            {getLocale("sublet.fromDate")}
          </Column>
          <Column
            body={(row: SubletWithHoldingOrLeaseAndApartmentData) =>
              (row.toDate && formatDate(row.toDate)) ?? "-"
            }
            field="toDate"
            filterableBy="date"
          >
            {getLocale("sublet.toDate")}
          </Column>
          <Column
            body={(row: SubletWithHoldingOrLeaseAndApartmentData) =>
              formatContractLength(row.fromDate, row.toDate, {
                year: t.year,
                years: t.years,
                month: t.month,
                months: t.months,
              })
            }
            field="contractLength"
            sortCallback={(
              a: SubletWithHoldingOrLeaseAndApartmentData,
              b: SubletWithHoldingOrLeaseAndApartmentData,
              _field: string,
              direction: "asc" | "desc",
            ) => compareContractLength(a, b, direction)}
          >
            {getLocale("sublet.contractLength")}
          </Column>
          <Column
            body={(row: SubletWithHoldingOrLeaseAndApartmentData) =>
              row.active ? t.yes : t.no
            }
            field="active"
            filterableBy="select"
            filterOptions={() => [
              { label: t.yes, value: true },
              { label: t.no, value: false },
            ]}
          >
            {getLocale("sublet.active")}
          </Column>
          <Column field="issueCount">{t.sublet.issueCount}</Column>
        </Table>
      </div>
    </>
  );
}
